import { useEffect, useState } from 'react';

import { useDebounce } from '~/hooks/use-debounce';
import type { OriginName } from '~/interfaces/common';

import { checkAliasesAvailability } from '../-api/api';
import type { AliasStatus } from '../-types/types';

export function useCheckAliases() {
  const [alias, setAlias] = useState('');
  const [metaAlias, setMetaAlias] = useState('');
  const [aliasStatus, setAliasStatus] = useState<AliasStatus>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [originName, setOriginName] = useState<OriginName>('' as OriginName);

  const debouncedAlias = useDebounce(alias, 1000);
  const isWaitingDebounce =
    alias.trim().length >= 3 && alias !== debouncedAlias;

  const isPhoneAlias =
    alias.startsWith('+') || /^\d{7,}$/.test(alias.replace(/\s/g, ''));

  useEffect(() => {
    if (!debouncedAlias.trim() || debouncedAlias.trim().length < 3) {
      setAliasStatus('idle');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const checkAlias = async () => {
      setIsLoading(true);
      const res = await checkAliasesAvailability(
        debouncedAlias.replace('+', ''),
      );

      if (cancelled) return;

      if (!res) {
        setAliasStatus('not_found');
        setIsLoading(false);
        return;
      }

      if ('message' in res) {
        setAliasStatus(
          res.message === 'E_ALIAS_NOT_VERIFIED' ? 'not_verified' : 'not_found',
        );
        setIsLoading(false);
        return;
      }

      setOriginName(res.origin);
      setMetaAlias(res.metadata.alias);
      setAliasStatus(res.status === 'active' ? 'active' : 'not_verified');
      setIsLoading(false);
    };

    checkAlias();

    return () => {
      cancelled = true;
    };
  }, [debouncedAlias]);

  const onChangeAlias = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAlias(e.currentTarget.value);
  };

  return {
    alias,
    metaAlias,
    originName,
    onChangeAlias,
    aliasStatus,
    isPhoneAlias,
    isRegistered: aliasStatus === 'active',
    isLoading: isLoading || isWaitingDebounce,
  };
}
